import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { FullPageLoading } from "@/components/common/Loading";
import { withNext } from "@/lib/safeReturn";

// Routes an unverified user is allowed to visit. Everything else bounces them
// to /verify-email. The HTTP middleware enforces the same rule on the server
// — this is just so the UI doesn't render dead screens.
const UNVERIFIED_ALLOWED = ["/verify-email", "/logout"];

// Routes an unfinished (account_complete=false) user is allowed to visit AFTER
// they've verified their email.
const ONBOARDING_ALLOWED = ["/welcome", "/dashboard/account", "/set-password"];

// Routes a user who still needs to set an initial password may visit.
const SET_PASSWORD_ALLOWED = ["/set-password", "/logout"];

const _pathIn = (pathname, paths) =>
  paths.some((p) => pathname === p || pathname.startsWith(`${p}/`));

export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoading />;
  // OAD-099 — keep the intended destination so an email link survives a sign-in.
  if (!user) {
    return <Navigate to={withNext("/login", `${location.pathname}${location.search || ""}`)}
      replace />;
  }
  // Step 1 — Email verification is the absolute first gate.
  if (!user.email_verified && !_pathIn(location.pathname, UNVERIFIED_ALLOWED)) {
    return <Navigate to="/verify-email" replace />;
  }
  // Step 2 — Every account must have a password (legacy OAuth accounts don't).
  if (user.email_verified && user.has_password === false &&
      !_pathIn(location.pathname, SET_PASSWORD_ALLOWED)) {
    return <Navigate to="/set-password" replace />;
  }
  // Step 3 — Account completion (first/last/country) is the next gate.
  if (user.email_verified && user.has_password !== false && !user.account_complete &&
      !_pathIn(location.pathname, ONBOARDING_ALLOWED)) {
    return <Navigate to="/welcome" replace />;
  }
  return children;
};

export const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.email_verified) return <Navigate to="/verify-email" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
};
